import React, { useState, useEffect } from 'react';
import './LoginPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const IS_ELECTRON = navigator.userAgent.toLowerCase().includes('electron');

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const [warmupPending, setWarmupPending] = useState(!IS_ELECTRON);

  // Forgot-password state: null | 'step1' | 'step2'
  const [forgotStep,    setForgotStep]    = useState(null);
  const [forgotUser,    setForgotUser]    = useState('');
  const [resetCode,     setResetCode]     = useState('');
  const [newPassword,   setNewPassword]   = useState('');
  const [forgotMsg,     setForgotMsg]     = useState('');
  const [forgotErr,     setForgotErr]     = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    if (IS_ELECTRON) return;
    let cancelled = false;
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 65_000);
    // Fire warmup in background (no-wait) so server pre-populates product cache
    fetch(`${API_URL}/api/warmup`).catch(() => {});
    fetch(`${API_URL}/api/health`, { signal: controller.signal })
      .catch(() => {})
      .finally(() => {
        clearTimeout(tid);
        if (!cancelled) setWarmupPending(false);
      });
    return () => { cancelled = true; clearTimeout(tid); controller.abort(); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Error al iniciar sesión');
      } else {
        localStorage.setItem('token', data.token);
        let role = 'admin';
        try {
          const b64 = data.token.split('.')[1] || '';
          const pay = JSON.parse(atob(b64.replace(/-/g, '+').replace(/_/g, '/')));
          role = pay.role || 'admin';
        } catch {}
        onLogin(role);
      }
    } catch {
      setError('No se pudo conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotStep1 = async (e) => {
    e.preventDefault();
    setForgotErr('');
    setForgotMsg('');
    setForgotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username: forgotUser.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setForgotErr(data.error || 'Error al enviar código'); return; }
      setForgotMsg(data.message || 'Código enviado. Revisa tu correo.');
      setForgotStep('step2');
    } catch {
      setForgotErr('No se pudo conectar con el servidor');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotStep2 = async (e) => {
    e.preventDefault();
    setForgotErr('');
    setForgotMsg('');
    if (newPassword.length < 6) { setForgotErr('La contraseña debe tener al menos 6 caracteres'); return; }
    setForgotLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username: forgotUser.trim(), code: resetCode.trim(), newPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setForgotErr(data.error || 'Código incorrecto o expirado'); return; }
      setForgotMsg('Contraseña actualizada. Ya puedes iniciar sesión.');
      setTimeout(() => { setForgotStep(null); setForgotUser(''); setResetCode(''); setNewPassword(''); setForgotMsg(''); }, 2500);
    } catch {
      setForgotErr('No se pudo conectar con el servidor');
    } finally {
      setForgotLoading(false);
    }
  };

  const brand = (
    <div className="login-brand">
      <img src="/logo_shopping_hunters.png" alt="Shopping Hunters" className="login-brand-logo" />
      <div className="login-brand-name">Shopping Hunters</div>
      <div className="login-brand-sub">Panel de control</div>
    </div>
  );

  if (forgotStep === 'step1') {
    return (
      <div className="login-root">
        <div className="login-card">
          {brand}
          <div className="login-title">Recuperar contraseña</div>
          <div className="login-subtitle">Ingresa tu nombre de usuario para recibir un código</div>
          <form className="login-form" onSubmit={handleForgotStep1}>
            <div className="login-field">
              <label className="login-label" htmlFor="forgot-user">Usuario</label>
              <input id="forgot-user" className="login-input" type="text" value={forgotUser}
                onChange={(e) => setForgotUser(e.target.value)} disabled={forgotLoading} required />
            </div>
            {forgotErr && <div className="login-error">{forgotErr}</div>}
            {forgotMsg && <div className="login-success">{forgotMsg}</div>}
            <button className="login-btn" type="submit" disabled={forgotLoading}>
              {forgotLoading ? 'Enviando…' : 'Enviar código'}
            </button>
            <button type="button" className="login-link" onClick={() => { setForgotStep(null); setForgotErr(''); setForgotMsg(''); }}>
              Volver al inicio de sesión
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (forgotStep === 'step2') {
    return (
      <div className="login-root">
        <div className="login-card">
          {brand}
          <div className="login-title">Nueva contraseña</div>
          <div className="login-subtitle">Ingresa el código recibido y tu nueva contraseña</div>
          <form className="login-form" onSubmit={handleForgotStep2}>
            <div className="login-field">
              <label className="login-label" htmlFor="reset-code">Código de 6 dígitos</label>
              <input id="reset-code" className="login-input" type="text" inputMode="numeric"
                maxLength={6} value={resetCode} onChange={(e) => setResetCode(e.target.value)}
                disabled={forgotLoading} required />
            </div>
            <div className="login-field">
              <label className="login-label" htmlFor="new-pass">Nueva contraseña</label>
              <input id="new-pass" className="login-input" type="password" autoComplete="new-password"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                disabled={forgotLoading} required />
            </div>
            {forgotErr && <div className="login-error">{forgotErr}</div>}
            {forgotMsg && <div className="login-success">{forgotMsg}</div>}
            <button className="login-btn" type="submit" disabled={forgotLoading}>
              {forgotLoading ? 'Guardando…' : 'Cambiar contraseña'}
            </button>
            <button type="button" className="login-link" onClick={() => setForgotStep('step1')}>
              Volver al paso anterior
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-root">
      <div className="login-card">
        {brand}
        <div className="login-title">Iniciar sesión</div>
        <div className="login-subtitle">Ingresa tus credenciales para continuar</div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label className="login-label" htmlFor="username">Usuario</label>
            <input
              id="username"
              className="login-input"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="password">Contraseña</label>
            <input
              id="password"
              className="login-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          {error && <div className="login-error">{error}</div>}

          <button className="login-btn" type="submit" disabled={loading}>
            {loading && warmupPending ? 'Iniciando…' : loading ? 'Ingresando…' : 'Ingresar'}
          </button>

          {loading && warmupPending && (
            <div className="login-warmup-hint">
              Despertando el servidor, esto puede tardar unos segundos la primera vez del día...
            </div>
          )}

          <button type="button" className="login-link" onClick={() => { setForgotStep('step1'); setForgotUser(username); setForgotErr(''); setForgotMsg(''); }}>
            ¿Olvidaste tu contraseña?
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
